import AnalyticsIcon from '@mui/icons-material/Analytics';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { Box, Button, Menu, MenuItem, Stack, TableCell, TableRow, Typography } from '@mui/material';
import { format } from 'date-fns';
import React from 'react';
import { useNavigate } from 'react-router';

import { DataTable } from 'src/components/DataTable';
import { ProjectFundingStatus } from 'src/global/enums/project.enum';
import { ProjectListItem } from 'src/global/schemas/project.schema';
import { colors } from 'src/global/styles/colors';

interface StagingListTableProps {
  nodeList: Array<ProjectListItem>;
  isLoading: boolean;
  page: number;
  limit: number;
}

const ProductionListTable = (props: StagingListTableProps) => {
  const { nodeList, isLoading, page, limit } = props;
  const nagivate = useNavigate();
  const [menuState, setMenuState] = React.useState<{ anchorEl: null | HTMLElement; selectedId: string | null }>({
    anchorEl: null,
    selectedId: null,
  });

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>, id: string) => {
    setMenuState({ anchorEl: event.currentTarget, selectedId: id });
  };

  const handleClose = () => {
    setMenuState({ anchorEl: null, selectedId: null });
  };

  const handleEditProd = () => {
    if (menuState.selectedId) {
      nagivate(`/core-sale/${menuState.selectedId}/edit`);
      handleClose();
    }
  };

  const handleGoToAnalytics = () => {
    if (menuState.selectedId) {
      nagivate(`/core-sale/${menuState.selectedId}/analytics`);
      handleClose();
    }
  };

  const handleNavigateDetail = (id: string) => {
    nagivate(`/core-sale/${id}`);
  };

  const renderFundingStatus = (status: ProjectFundingStatus) => {
    const statusConfig = {
      [ProjectFundingStatus.LOCK]: {
        text: 'Lock',
        styles: {
          backgroundColor: colors.neutral06,
        },
      },
      [ProjectFundingStatus.UNLOCK]: {
        text: 'Unlock',
        styles: {
          color: colors.green300,
          backgroundColor: colors.green3006,
        },
      },
      [ProjectFundingStatus.REFUND]: {
        text: 'Refund',
        styles: {
          color: colors.red300,
          backgroundColor: colors.red3004,
        },
      },
    };

    const { text, styles } = statusConfig[status] || {
      text: 'Unknown',
      styles: {},
    };

    return (
      <Box
        sx={{
          borderRadius: '4px',
          padding: '8px 12px',
          fontSize: '14px',
          fontWeight: 500,
          lineHeight: '20px',
          width: 'fit-content',
          ...styles,
        }}>
        {text}
      </Box>
    );
  };

  return (
    <DataTable
      isEmpty={!nodeList.length && !isLoading}
      isLoading={isLoading}
      skeletonRowCount={4}
      renderHeader={() => (
        <>
          <TableRow
            sx={{
              backgroundColor: colors.neutral06,
            }}>
            <TableCell
              rowSpan={2}
              sx={{
                paddingLeft: '56px',
              }}>
              Project Name
            </TableCell>
            <TableCell rowSpan={2}>Last Updated</TableCell>
            <TableCell rowSpan={2}>Start Sale Date</TableCell>
            <TableCell rowSpan={2}>Funding Status</TableCell>
            <TableCell rowSpan={2}>Current Tier</TableCell>
            <TableCell rowSpan={1}>Total Supply</TableCell>
            <TableCell rowSpan={1}>Total Sold</TableCell>
          </TableRow>
        </>
      )}
      renderBody={() => (
        <>
          {nodeList.map((row, index) => {
            return (
              <TableRow key={row.id}>
                <TableCell>
                  <Stack direction={'row'} alignItems={'center'}>
                    <Typography fontSize={'14px'} width={'40px'}>
                      {(page - 1) * limit + index + 1}
                    </Typography>
                    <Stack>
                      <Typography
                        sx={{ cursor: 'pointer' }}
                        component={'p'}
                        onClick={() => handleNavigateDetail(row.id)}
                        fontSize={'14px'}>
                        {row.name}
                      </Typography>
                      <Typography
                        component={'a'}
                        fontSize={'12px'}
                        color={colors.blue75}
                        textOverflow={'ellipsis'}
                        href={row.nodeSaleUrl}
                        target={'_blank'}>
                        {row.nodeSaleUrl}
                      </Typography>
                    </Stack>
                  </Stack>
                </TableCell>
                <TableCell>{format(new Date(row.updatedAt), 'dd/MM/yyyy')}</TableCell>
                <TableCell>{format(new Date(row.releaseAt!), 'dd/MM/yyyy H:mm')}</TableCell>
                <TableCell align='center'>
                  {row.setting?.fundingStatus ? renderFundingStatus(row.setting.fundingStatus) : null}
                </TableCell>
                <TableCell align='center'>{Number(row.setting?.currentTier ?? 0) + 1}</TableCell>
                <TableCell align='center'>{row.setting?.totalSupply}</TableCell>

                <TableCell align='justify'>
                  <Box width={'100%'} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography>{row.setting?.totalSold}</Typography>
                    <Button disableRipple onClick={e => handleClick(e, row.id)}>
                      <MoreHorizIcon sx={{ color: 'white' }} />
                    </Button>
                    <Menu
                      id={`menu-${row.id}`}
                      anchorEl={menuState.anchorEl}
                      open={Boolean(menuState.anchorEl && menuState.selectedId === row.id)}
                      onClose={handleClose}
                      sx={{
                        '& .MuiPaper-root': {
                          backgroundColor: 'black',
                          color: 'white',
                          borderRadius: '8px',
                        },
                        '& .MuiMenuItem-root': {
                          '&:hover': {
                            opacity: 0.7,
                          },
                        },
                      }}
                      MenuListProps={{
                        sx: {
                          bgcolor: 'black',
                          color: 'white',
                          borderRadius: '8px',
                        },
                      }}>
                      <MenuItem
                        onClick={handleEditProd}
                        sx={{ padding: '8px', display: 'flex', flexDirection: 'column' }}>
                        <Box width={'100%'} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'start' }}>
                          <BorderColorIcon
                            sx={{ width: '20px', height: '20px', color: 'white', marginRight: '12px' }}
                          />
                          <Typography fontSize={'14px'} fontWeight={400} lineHeight={'20px'}>
                            Edit Production
                          </Typography>
                        </Box>
                      </MenuItem>
                      {/* <MenuItem
                        onClick={handleGoToAnalytics}
                        sx={{ padding: '8px', display: 'flex', flexDirection: 'column' }}>
                        <Box width={'100%'} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'start' }}>
                          <AnalyticsIcon sx={{ width: '20px', height: '20px', color: 'white', marginRight: '12px' }} />
                          <Typography fontSize={'14px'} fontWeight={400} lineHeight={'20px'}>
                            Analytics
                          </Typography>
                        </Box>
                      </MenuItem> */}
                    </Menu>
                  </Box>
                </TableCell>
              </TableRow>
            );
          })}
        </>
      )}
    />
  );
};

export { ProductionListTable };
